"use client";

import { cn } from "@faire/react/tools/cn";
import { getSlots } from "@faire/slate/lib/getSlots";
import { ModalBase } from "@faire/slate/private/modal/ModalBase";
import { ModalClose } from "@faire/slate/private/modal/ModalClose";
import { ModalHeaderContext } from "@faire/slate/private/modal/ModalHeaderContext";
import { ModalOverlay } from "@faire/slate/private/modal/ModalOverlay";
import { ModalPortal } from "@faire/slate/private/modal/ModalPortal";
import { ModalRoot } from "@faire/slate/private/modal/ModalRoot";
import { ModalScrollArea } from "@faire/slate/private/modal/ModalScrollArea";
import { ModalScrollContext } from "@faire/slate/private/modal/ModalScrollContext";
import { ModalSizeVariant } from "@faire/slate/private/modal/types";
import React from "react";

import { BaseModalBody } from "./BaseModalBody";
import { BaseModalFooter } from "./BaseModalFooter";
import { BaseModalHeader } from "./BaseModalHeader";

type IProps = {
  /**
   * Whether to disable animations for the modal.
   */
  disableAnimations?: boolean;

  /**
   * Modal size variant
   *
   * @default "small"
   */
  size?: ModalSizeVariant;

  /**
   * Whether the modal is open
   */
  open?: boolean;

  /**
   * **web**
   * Function to close the modal
   */
  onClose?: () => void;

  /**
   * **web**
   * The data-test-id of the modal dialog element.
   */
  "data-test-id"?: string;
};

/**
 * Used for the majority of modality use cases, like presenting overflow text and forms.
 *
 * ```tsx
 * import { Button } from "@faire/slate/components/button";
 * import {
 *   BaseModal,
 *   BaseModalHeader,
 *   BaseModalBody,
 *   BaseModalFooter,
 * } from "@faire/slate/components/modal/base";
 *
 * <BaseModal open onClose={handleClose}>
 *   <BaseModalHeader title="Modal Title" />
 *   <BaseModalBody>
 *     Modal content, it can be anything
 *   </BaseModalBody>
 *   <BaseModalFooter>
 *     <Button>Close</Button>
 *     <Button variant="secondary">Cancel</Button>
 *   </BaseModalFooter>
 * </BaseModal>
 * ```
 *
 * See [migration guide](../migration-guide.mdx) for more information.
 *
 * {@link https://www.notion.so/faire/Base-Modal-2ca2efb5c25a80c0ae3fc4e5d179e002 | Slate/BaseModal}
 */
export const BaseModal: React.FC<React.PropsWithChildren<IProps>> = ({
  children,
  size = "small",
  "data-test-id": dataTestId,
  ...props
}) => {
  const [{ header, body, footer }, rest] = getSlots(children, {
    header: BaseModalHeader,
    body: BaseModalBody,
    footer: BaseModalFooter,
  });

  if (rest.length > 0) {
    // oxlint-disable-next-line no-console
    console.error(
      "Modal accepts only ModalHeader, ModalBody, and ModalFooter as children."
    );
  }

  if (!header) {
    // oxlint-disable-next-line no-console
    console.error("Modal must have a ModalHeader as a child.");
  }

  return (
    <ModalRoot {...props} width={size} key={size}>
      <ModalPortal>
        <ModalOverlay>
          <ModalScrollContext>
            <ModalHeaderContext>
              <ModalBase
                data-component-name="Slate/BaseModal"
                data-test-id={dataTestId}
                className={cn(
                  "fs-modal--base",
                  !footer && "fs-modal--footerless"
                )}
              >
                <ModalScrollArea>
                  {header}
                  {body}
                </ModalScrollArea>
                {footer}
                {/* Note: The close button is inconsistent with Figma, this one has an absolute position, */}
                {/* this is intentional because we want the close button to have the last focusing priority*/}
                <div className="fs-modal__close">
                  <ModalClose />
                </div>
              </ModalBase>
            </ModalHeaderContext>
          </ModalScrollContext>
        </ModalOverlay>
      </ModalPortal>
    </ModalRoot>
  );
};
